import ko from 'knockout';
import _ from 'underscore';

/**
 * Get validators validate failed for value
 * @param {Validator[]} validators - array of validator
 * @param {any} value - value to be validated
 * @returns {Validator[]} - array of failed validator
 */
function failedValidators(validators, value) {
  return _.filter(validators, v => !v.isValid(value));
}

/**
 * Get error messages from validators for value
 * @param {Validator[]} validators - array of validator
 * @param {any} value - the value to be validated with validators
 * @returns {string[]} - list error message of failed validators
 */
function errorMessages(validators, value) {
  let errors = [];

  _.each(validators, (failed) => {
    const messages = _.isFunction(failed.message) ? failed.message(value) : failed.message;

    if (_.isArray(messages)) {
      errors = errors.concat(messages);
    } else {
      errors.push(messages);
    }
  });

  return errors;
}

/**
 * Extend validate function for knockout
 * @param {Observable} observable - observable to be validated
 * @param {Validator[]} validators - array of validator
 * @returns {Observable} - return a wrapped obsverable with read and write inject
 */
ko.extenders.validate = (observable, validators) => {
  const observableWrapper = ko.computed({
    read() {
      return observable();
    },
    write(rawValue) {
      let value = rawValue;
      let errors = [];

      _.each(validators, (validator) => {
        if (_.isFunction(validator.process) && !validator.blockInput) {
          value = validator.process(value);
        }
      });

      const failedValidations = failedValidators(validators, value);

      if (_.isEmpty(failedValidations)) {
        observableWrapper.hasBeenValid(true);
      }

      // if user input is blocked, do not pass this value to internal object
      if (_.some(failedValidations, _.property('blockInput'))) {
        observable.notifySubscribers();
      } else {
        // only show errors after observable has been valid
        // friendly to user who need to input several times to get a valid value from beginning
        // for example, an array with at least 3 elements, or a number > 10
        if (observableWrapper.hasBeenValid()) {
          errors = errorMessages(failedValidations, value);
        }

        if (_.isEqual(observable(), value)) {
          observable.notifySubscribers();
        } else {
          observable(value);
        }
      }

      observableWrapper.errors(errors);
    },
  }).extend({ notify: 'always' });

  observableWrapper.hasBeenValid =
    ko.observable(_.isEmpty(failedValidators(validators, observable())));
  observableWrapper.errors = ko.observableArray();
  observableWrapper.validators = validators;
  observableWrapper.isValid = ko.computed(() => observableWrapper.errors().length === 0);
  observableWrapper.hasError = ko.computed(() => !observableWrapper.isValid());

  // force validation even if value not changed
  observableWrapper.validate = () => {
    observableWrapper.hasBeenValid(true);

    const value = observable();

    observableWrapper.errors(errorMessages(failedValidators(validators, value), value));

    return observableWrapper.isValid();
  };

  return observableWrapper;
};
